import React, { useState, useRef, useEffect } from 'react';
import { EntityAssistantDrawer } from '../../components/assistant/EntityAssistantDrawer.js';
import { Save, Play, Columns, Layers, AlertTriangle, Sparkles, Upload, ChevronDown } from 'lucide-react';
import type { ConfigTab } from './WorkflowBuilderTypes.js';
import { WorkflowPolicyCard } from '../../components/workflow/WorkflowPolicyCard.js';
import { WorkflowSettingsPanel } from './WorkflowSettingsPanel.js';
import { ExecutionFlowPanel } from './ExecutionFlowPanel.js';
import { StepConfigurationPanel } from './StepConfigurationPanel.js';
import { WorkflowBuilderTabs } from './WorkflowBuilderTabs.js';
import { NodePalette } from '../../components/workflow/NodePalette.js';
import { Button, Callout, Modal, PageTitle } from '../../components/ui';
import { WorkspacePage } from '../../components/page';
import { PendingChangesModal } from '../../components/workflow/PendingChangesModal.js';
import { ExportInstructionsModal } from '../../components/workflow/ExportInstructionsModal.js';
import { ConflictDialog } from '../../components/workflow/ConflictDialog.js';
import { NavigateAwayWarning } from '../../components/entity/NavigateAwayWarning.js';
import { useWorkflowBuilderState } from '../../hooks/useWorkflowBuilderState.js';
import { buildWorkflowAssistantImportMessage } from '../../lib/workflow-assistant-import.js';
import { applyWorkflowAssistantPatch } from '../../lib/workflow-assistant-apply.js';
import { usePolicy } from '../../hooks/usePolicy.js';

// ==========================================
// Main Component
// ==========================================

interface WorkflowBuilderPageProps {
  panelMode?: boolean;
  idOverride?: string;
}

export const WorkflowBuilderPage: React.FC<WorkflowBuilderPageProps> = ({ panelMode = false, idOverride }) => {
  const {
    id,
    isNew,
    wf,
    changes,
    selectedStep,
    setSelectedStep,
    sel,
    activeTab,
    setActiveTab,
    layoutMode,
    setLayoutMode,
    focusMode,
    setFocusMode,
    collapsedSections,
    setCollapsedSections,
    dagCollapsed,
    stepsCollapsed,
    dagExpanded,
    stepsExpanded,
    toggleSection,
    addStep,
    removeStep,
    updateStep,
    stepLabels,
    stepResults,
    setStepResults,
    diagramSteps,
    promptPreview,
    promptLoading,
    instructionMode,
    setInstructionMode,
    roles,
    multiStream,
    handlePositionsChange,
    dragOver,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    assistantMessage,
    setAssistantMessage,
    pendingChangesOpen,
    setPendingChangesOpen,
    exportOpen,
    setExportOpen,
    save,
    confirmSaveWithWarnings,
    saveWarnings,
    setSaveWarnings,
    trigger,
    sendNegotiateMessage,
    debugStatuses,
    agentApi,
    stepWarnings,
    addEdge,
    removeEdge,
    handleDropStep,
    saveGraphError,
    clearSaveGraphError,
  } = useWorkflowBuilderState(idOverride);

  const canManageWorkflows = usePolicy('CanManageWorkflows');
  const canExecuteWorkflows = usePolicy('CanExecuteWorkflows');

  const canvasEditable = !isNew && canManageWorkflows;

  const hasIssues = Object.keys(stepWarnings).length > 0;

  const [issuesOpen, setIssuesOpen] = useState(false);
  const issuesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (issuesRef.current && !issuesRef.current.contains(e.target as Node)) {
        setIssuesOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const flowToggleLabel = layoutMode === 'classic' ? 'Switch to tab layout' : 'Switch to classic layout';
  const title =
    focusMode && sel ? sel.name || 'Untitled step' : isNew ? 'New workflow' : wf.form.name || 'Untitled workflow';

  const header = (
    <>
      {panelMode ? (
        <h2 className="truncate text-lg font-semibold text-heading">{title}</h2>
      ) : (
        <PageTitle title={title} backTo="/agents/workflows" backLabel="Back to workflows" />
      )}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setLayoutMode(layoutMode === 'classic' ? 'tabs' : 'classic')}
          title={flowToggleLabel}
        >
          {layoutMode === 'classic' ? (
            <>
              <Columns size={14} /> Classic
            </>
          ) : (
            <>
              <Layers size={14} /> Tabs
            </>
          )}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setExportOpen(true)}
          title="Export instructions to external AI, then import results back — a two-way collaboration flow"
        >
          <Sparkles size={14} /> AI collaborate
        </Button>
        {!isNew && wf.form.status === 'active' && canExecuteWorkflows && (
          <Button variant="success" size="sm" onClick={trigger}>
            <Play size={14} /> Run
          </Button>
        )}
        {wf.isDirty && canManageWorkflows && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPendingChangesOpen(true)}
            title="View pending changes"
            className="border-warning text-warning"
          >
            <AlertTriangle size={14} /> Unsaved
          </Button>
        )}
        <div className="relative" ref={issuesRef}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => hasIssues && setIssuesOpen((o) => !o)}
            disabled={!hasIssues}
            className={hasIssues ? 'border-warning text-warning' : 'border-success/40 text-success'}
          >
            {hasIssues ? 'Issues' : 'No issues'} {hasIssues && <ChevronDown size={13} />}
          </Button>
          {hasIssues && issuesOpen && (
            <div className="absolute right-0 top-full z-50 mt-1 min-w-[200px] rounded-lg border bg-surface py-1 shadow-lg">
              <div className="px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted">Actions</div>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setIssuesOpen(false);
                  sendNegotiateMessage();
                }}
              >
                Negotiate contracts
              </Button>
            </div>
          )}
        </div>
        {canManageWorkflows && (
          <Button variant="primary" size="sm" onClick={save} loading={wf.saving}>
            {!wf.saving && <Save size={14} />} {wf.saving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </div>
    </>
  );

  return (
    <div
      className="relative h-full overflow-hidden"
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drop overlay */}
      {dragOver && (
        <div className="absolute inset-0 z-50 bg-accent/10 border-2 border-dashed border-accent rounded-xl flex items-center justify-center backdrop-blur-xs">
          <div className="flex flex-col items-center gap-3 text-accent-text">
            <Upload size={40} />
            <span className="text-lg font-semibold">Drop file to import from external AI</span>
            <span className="text-sm text-muted">Supports .txt, .md, .json, .yaml files</span>
          </div>
        </div>
      )}

      {/* Workflow builder: workspace template (demo /email, /task/kanban). Embedded panels keep their parent's height. */}
      <WorkspacePage header={header} className={panelMode ? 'lg:h-full' : undefined}>
        <div className="flex h-full min-h-0 flex-col gap-4">
          {saveGraphError && (
            <Callout
              tone="danger"
              icon={AlertTriangle}
              className="shrink-0"
              actions={
                <Button variant="ghost" size="sm" onClick={clearSaveGraphError}>
                  Dismiss
                </Button>
              }
            >
              {saveGraphError}
            </Callout>
          )}

          {/* Content area */}
          {layoutMode === 'tabs' ? (
            <WorkflowBuilderTabs
              form={wf.form}
              updateField={wf.updateField}
              diagramSteps={diagramSteps}
              onDirectionToggle={() =>
                wf.updateField('flowDirection', wf.form.flowDirection === 'vertical' ? 'horizontal' : 'vertical')
              }
              onPositionsChange={handlePositionsChange}
              steps={wf.form.steps}
              selectedStep={selectedStep}
              sel={sel}
              stepLabels={stepLabels}
              stepResults={stepResults}
              onSelectStep={setSelectedStep}
              onAddStep={addStep}
              onRemoveStep={removeStep}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              roles={roles}
              onUpdateStep={updateStep}
              promptPreview={promptPreview}
              promptLoading={promptLoading}
              instructionMode={instructionMode}
              onInstructionModeChange={setInstructionMode}
              onOpenPromptBuilder={() => {}}
              templateId={id}
              isNew={isNew}
              multiStream={multiStream}
              onDebugResult={(stepId, result) => setStepResults((prev) => ({ ...prev, [stepId]: result }))}
              onFocusToggle={setFocusMode}
              agentApi={agentApi}
              changes={changes}
              debugStatuses={debugStatuses}
              stepWarnings={stepWarnings}
            />
          ) : (
            // One scroll view: each section keeps a usable height and the column scrolls, so opening Settings pushes
            // the flow and the steps down instead of squeezing them to nothing (owner 2026-10-17).
            <div className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
              {!focusMode && !isNew && id && <WorkflowPolicyCard workflowId={Number(id)} />}

              {/* Settings */}
              {!focusMode && (
                <WorkflowSettingsPanel
                  collapsed={collapsedSections.has('metadata')}
                  onToggle={() => toggleSection('metadata')}
                  form={wf.form}
                  updateField={wf.updateField}
                  changes={changes}
                  templateId={!isNew && id ? Number(id) : undefined}
                />
              )}

              {/* Execution Flow — with NodePalette when canvas is editable */}
              {!focusMode && wf.form.steps.length > 0 && (
                <div className={dagCollapsed ? 'flex shrink-0 gap-4' : 'flex min-h-88 flex-1 gap-4'}>
                  {canvasEditable && !dagCollapsed && (
                    <NodePalette
                      onAddStep={(kind, _executionMode) =>
                        handleDropStep(kind, { x: 50, y: 50 + wf.form.steps.length * 80 })
                      }
                    />
                  )}
                  <ExecutionFlowPanel
                    collapsed={dagCollapsed}
                    expanded={dagExpanded}
                    onToggle={() => toggleSection('dag')}
                    onExpandToggle={() =>
                      setCollapsedSections(dagExpanded ? new Set() : new Set(['steps', 'metadata']))
                    }
                    diagramSteps={diagramSteps}
                    selectedStepId={selectedStep}
                    onStepClick={(sid) => {
                      setSelectedStep(sid);
                    }}
                    onOutputClick={(sid) => {
                      setSelectedStep(sid);
                      setActiveTab('output' as ConfigTab);
                    }}
                    flowDirection={wf.form.flowDirection}
                    onDirectionToggle={() =>
                      wf.updateField('flowDirection', wf.form.flowDirection === 'vertical' ? 'horizontal' : 'vertical')
                    }
                    onPositionsChange={handlePositionsChange}
                    editable={canvasEditable}
                    formEdges={wf.form.edges}
                    onAddEdge={addEdge}
                    onRemoveStep={removeStep}
                    onRemoveEdge={removeEdge}
                    onDropStep={handleDropStep}
                    onUpdateStep={(sid, updates) => updateStep(sid, updates)}
                  />
                </div>
              )}

              {/* Steps & Configuration */}
              <StepConfigurationPanel
                stepsCollapsed={stepsCollapsed}
                stepsExpanded={stepsExpanded}
                focusMode={focusMode}
                onExpandToggle={() => {
                  if (stepsExpanded) {
                    setCollapsedSections(new Set());
                  } else {
                    setCollapsedSections(new Set(['dag', 'metadata']));
                  }
                }}
                onToggle={() => toggleSection('steps')}
                steps={wf.form.steps}
                selectedStep={selectedStep}
                sel={sel}
                stepLabels={stepLabels}
                stepResults={stepResults}
                onSelectStep={setSelectedStep}
                onAddStep={addStep}
                onRemoveStep={removeStep}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                roles={roles}
                onUpdateStep={updateStep}
                promptPreview={promptPreview}
                promptLoading={promptLoading}
                instructionMode={instructionMode}
                onInstructionModeChange={setInstructionMode}
                onOpenPromptBuilder={() => {}}
                templateId={id}
                isNew={isNew}
                multiStream={multiStream}
                onDebugResult={(stepId, result) => setStepResults((prev) => ({ ...prev, [stepId]: result }))}
                onFocusToggle={setFocusMode}
                agentApi={agentApi}
                changes={changes}
                debugStatuses={debugStatuses}
                stepWarnings={stepWarnings}
              />
            </div>
          )}
        </div>
      </WorkspacePage>

      {/* Workflow AI Assistant — Spec 64 Phase B re-adoption.
          Sibling of the layout div so the fixed panel overlays the full viewport.
          subjectId is the route :id (string | undefined); when isNew it falls
          back to undefined and the drawer treats it as a draft-key session.
          Suppressed in panelMode — the panel's chat thread IS the assistant. */}
      {!panelMode && (
        <EntityAssistantDrawer
          subjectType="workflow"
          subjectId={!isNew && id ? id : undefined}
          draftKey={isNew ? 'workflow-new' : (id ?? 'workflow-new')}
          entryPoint="workflow-builder"
          currentFormValues={
            {
              workflowState: wf.form,
              currentStepName: sel?.name ?? undefined,
              currentStepKey: selectedStep ?? undefined,
              planMode: false,
              // `department`/`name` were always undefined here — AgentTemplateSummary has no
              // `department` (it's `departmentId`) or `name` (it's `title`) field, so the AI
              // assistant's role context lost department grouping silently. Fixed to the real field names.
              roles: roles.map((role) => ({
                id: role.slug ?? role.id,
                title: role.title ?? role.slug,
                department: role.departmentId,
              })),
            } as Record<string, unknown>
          }
          currentStepKey={selectedStep ?? undefined}
          queuedMessage={assistantMessage}
          onQueuedMessageConsumed={() => setAssistantMessage(null)}
          onApplyFieldUpdate={(partial) => {
            wf.setForm(applyWorkflowAssistantPatch(wf.form, partial));
          }}
        />
      )}

      {/* Pending Changes Modal */}
      <PendingChangesModal
        open={pendingChangesOpen}
        onClose={() => setPendingChangesOpen(false)}
        onDiscard={wf.discardDraft}
        diffs={pendingChangesOpen ? wf.getDiff() : []}
      />

      {/* Export Instructions Modal */}
      <ExportInstructionsModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        workflowId={id}
        steps={wf.form.steps}
        selectedStepId={selectedStep}
        onImport={(content, fileName) => {
          setAssistantMessage(buildWorkflowAssistantImportMessage(content, fileName));
          setExportOpen(false);
        }}
      />

      {/* Conflict Resolution Dialog */}
      {wf.conflict && (
        <ConflictDialog
          conflict={wf.conflict}
          onReload={() => wf.resolveConflict('reload')}
          onForceSave={() => wf.resolveConflict('force')}
        />
      )}

      {/* Unsaved-changes guard. `useWorkflowForm` blocks the navigation via `useDirtyGuard`;
          this is the half that asks the question. It used to be a `window.confirm()` inside the
          hook — which blocked the event loop, so the browser-automation suite could not drive
          past it, and could not be styled. */}
      <NavigateAwayWarning
        open={wf.navGuard.pendingNavigation}
        onLeave={wf.navGuard.confirmLeave}
        onStay={wf.navGuard.cancelLeave}
      />

      {/* Save Warnings Dialog */}
      <Modal
        open={saveWarnings.length > 0}
        onClose={() => setSaveWarnings([])}
        title="Workflow has warnings"
        titleIcon={<AlertTriangle size={16} className="text-warning" />}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setSaveWarnings([])}>
              Cancel
            </Button>
            {saveWarnings.some((w) => w.action?.type === 'negotiate') && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSaveWarnings([]);
                  sendNegotiateMessage();
                }}
              >
                Negotiate
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={confirmSaveWithWarnings}>
              Save anyway
            </Button>
          </>
        }
      >
        <ul className="space-y-2 max-h-60 overflow-auto">
          {saveWarnings.map((w, i) => (
            <li key={i} className="flex items-center gap-2 text-xs">
              <span className="text-warning mt-0.5 shrink-0">&#x2022;</span>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-heading">{w.stepName}:</span>{' '}
                <span className="text-body">{w.message}</span>
              </div>
              {w.action && w.stepId && w.action.type === 'navigate' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSaveWarnings([]);
                    setSelectedStep(w.stepId!);
                    if (w.action!.tab) setActiveTab(w.action!.tab);
                  }}
                >
                  {w.action.label}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );
};
